<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">📎 {{ $t("documents.title") }}</h4>

      <form @submit.prevent="handleUpload">
        <div class="row">
          <!-- Document Type -->
          <div class="col-md-6 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("documents.fields.type") }}
            </label>
            <select v-model="form.document_type_id" class="form-select" required>
              <option disabled value="">{{ $t("documents.placeholders.select") }}</option>
              <option v-for="type in documentTypes" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>

          <!-- Title -->
          <div class="col-md-6 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("documents.fields.title") }}
            </label>
            <input type="text" v-model="form.title" class="form-control" :placeholder="$t('documents.placeholders.title')" />
          </div>

          <!-- File Upload -->
          <div class="col-md-12 mb-4">
            <label class="form-label fw-bold" style="color: #b03a45;">
              {{ $t("documents.fields.file") }}
            </label>
            <input type="file" @change="handleFileChange" class="form-control" required />
          </div>

          <!-- Upload Button -->
          <div class="d-flex justify-content-end">
            <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
              {{ $t("documents.buttons.upload") }}
            </button>
          </div>

          <!-- Save Button -->
          <div class="d-flex justify-content-end mt-2">
            <button type="button" @click="handleSave" class="btn text-white px-4" style="background-color: #b03a45;">
              {{ $t("documents.buttons.save") }}
            </button>
          </div>
        </div>
      </form>

      <hr class="my-4" />

      <h5 class="mb-3 fw-bold" style="color: #b03a45;">
        <i class="fas fa-folder-open me-2"></i>{{ $t("documents.uploadedTitle") }}
      </h5>

      <ul v-if="uploadedDocuments.length" class="list-group">
        <li
          v-for="doc in uploadedDocuments"
          :key="doc.id"
          class="list-group-item d-flex justify-content-between align-items-center"
        >
          <span>
            <i class="fas fa-file-alt text-secondary me-2"></i>
            {{ doc.title || doc.type.name }}
          </span>
          <button @click="handleDelete(doc.id)" class="btn btn-sm btn-outline-danger">
            {{ $t("documents.buttons.delete") }}
          </button>
        </li>
      </ul>
      <p v-else class="text-muted">{{ $t("documents.noDocs") }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import axios from '@/api'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const toast = useToast()

const userId = route.query.user_id
const documentTypes = ref([])
const uploadedDocuments = ref([])

const form = ref({
  user_id: userId,
  document_type_id: '',
  title: '',
  file: null,
})

const fetchDocumentTypes = async () => {
  try {
    // Temporaire: utiliser des données statiques car la route n'existe pas encore
    const staticTypes = [
      { id: 1, name: 'CV' },
      { id: 2, name: 'Lettre de motivation' },
      { id: 3, name: 'Contrat de travail' },
      { id: 4, name: 'Bulletin de paie' },
      { id: 5, name: 'Carte d\'identité' },
      { id: 6, name: 'Passeport' },
      { id: 7, name: 'Permis de conduire' },
      { id: 8, name: 'Diplôme' },
      { id: 9, name: 'Certificat de travail' },
      { id: 10, name: 'RIB' },
      { id: 11, name: 'Autre' }
    ]
    documentTypes.value = staticTypes
  } catch (error) {
    console.error(error)
    toast.error(t("documents.messages.loadTypesFailed"))
  }
}

const fetchUploadedDocuments = async () => {
  try {
    const { data } = await axios.get(`/employee/documents/${userId}`)
    if (data.message && data.message.includes('No documents found')) {
      uploadedDocuments.value = []
      toast.info(data.message)
    } else {
      uploadedDocuments.value = data.data || []
    }
  } catch (error) {
    if (error.response?.status === 404) {
      uploadedDocuments.value = []
      toast.info('Cet utilisateur n\'a pas de documents')
    } else {
      console.error(error)
      toast.error(t("documents.messages.loadDocsFailed"))
    }
  }
}

const handleFileChange = (e) => {
  const file = e.target.files?.[0]
  if (file) {
    form.value.file = file
  }
}

const handleUpload = async () => {
  try {
    const payload = new FormData()
    for (const key in form.value) {
      if (form.value[key]) {
        payload.append(key, form.value[key])
      }
    }

    await axios.post('/employee/documents', payload, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    toast.success(t("documents.messages.uploadSuccess"))
    await fetchUploadedDocuments()

    // reset form
    form.value.document_type_id = ''
    form.value.title = ''
    form.value.file = null
  } catch (error) {
    console.error(error)
    toast.error(t("documents.messages.uploadFailed"))
  }
}

const handleDelete = async (id) => {
  try {
    await axios.delete(`/employee/documents/${id}`)
    toast.success(t("documents.messages.deleteSuccess"))
    await fetchUploadedDocuments()
  } catch (error) {
    console.error(error)
    toast.error(t("documents.messages.deleteFailed"))
  }
}

const handleSave = () => {
  router.push('/employees')
}

onMounted(() => {
  fetchDocumentTypes()
  fetchUploadedDocuments()
})
</script>
